/**
 * Inline editor for report-setup Coverage Matrix rows — persists to
 * site.typedPreferencesDefaults via the store action.
 */
import React from 'react';
import type { ChecklistItem } from '../../../config/documentChecklists';
import type { ReportTypeId } from '../../../config/reportTypes';
import type { TypedPreferencesByType, ProjectIntent } from '../../../types/siteRag';
import type { SiteEntity } from '../../../types/site';
import type { SiteEvidenceDocument } from '../../../types/siteEvidence';
import {
  makeEmptyForm,
  type AuthoredEvidenceFormState,
} from '../../../types/authoredEvidence';
import {
  resolveReportSetupFields,
  setupFormValuesToPreferences,
} from '../../../lib/coverage/resolveReportSetupFields';
import { setupReportTypeForRequirement } from '../../../config/reportSetupRequirements';
import { AuthoredFieldInput } from './AuthoredFieldInput';
import { useStore } from '../../../store';

export interface RequirementInlineSetupFormProps {
  siteId: string;
  site?: Pick<SiteEntity, 'id' | 'name' | 'siteArea' | 'address' | 'projectIntent' | 'typedPreferencesDefaults'>;
  item: ChecklistItem;
  reportType: ReportTypeId;
  typedPreferences?: TypedPreferencesByType | null;
  projectIntent?: ProjectIntent;
  sed?: SiteEvidenceDocument | null;
  /** When set, overrides the default inline-setup test id (wizard required vs optional). */
  testId?: string;
  fieldKeys: readonly string[];
  onSaved?: () => void;
}

export interface RequirementInlineSetupFormHandle {
  /** Persist dirty inline edits; returns false when save failed. */
  saveIfDirty: () => Promise<boolean>;
}

function seedSetupForm(
  item: ChecklistItem,
  reportType: ReportTypeId,
  typedPreferences?: TypedPreferencesByType | null,
  projectIntent?: ProjectIntent,
  site?: RequirementInlineSetupFormProps['site'],
  sed?: SiteEvidenceDocument | null,
): AuthoredEvidenceFormState {
  const schema = item.authoredSchema ?? [];
  const base = makeEmptyForm(schema);
  const resolved = resolveReportSetupFields({
    requirement: item,
    reportType,
    typedPreferences,
    projectIntent,
    site: site as SiteEntity | undefined,
    sed,
  });
  for (const f of resolved.primary) {
    if (f.isEmpty || f.value === undefined) continue;
    if (typeof f.value === 'boolean') base.fields[f.key] = f.value;
    else if (typeof f.value === 'number' || typeof f.value === 'string') base.fields[f.key] = f.value;
  }
  return base;
}

export const RequirementInlineSetupForm = React.forwardRef<
  RequirementInlineSetupFormHandle,
  RequirementInlineSetupFormProps
>(({
  siteId,
  site,
  item,
  reportType,
  typedPreferences,
  projectIntent,
  sed,
  fieldKeys,
  onSaved,
  testId = 'coverage-requirement-inline-setup',
}, ref) => {
  const updateSiteTypedPreferencesDefault = useStore(s => s.updateSiteTypedPreferencesDefault);
  const schema = item.authoredSchema ?? [];
  const editableFields = schema
    .filter(f => f.key !== 'notes' && f.readOnly !== true && fieldKeys.includes(f.key));

  const [form, setForm] = React.useState<AuthoredEvidenceFormState>(() =>
    seedSetupForm(item, reportType, typedPreferences, projectIntent, site, sed),
  );
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [dirty, setDirty] = React.useState(false);
  // Review S3 (2026-09-05) — keys the USER edited in this form session. Only
  // these are persisted: the seed also carries DERIVED defaults, and writing the whole
  // form on an unrelated edit froze such a default into an authored
  // declaration — which P1.10 treats as the owner's decision.
  const touchedKeysRef = React.useRef<Set<string>>(new Set());
  const savePromiseRef = React.useRef<Promise<boolean> | null>(null);
  const scopeRef = React.useRef(`${siteId}:${item.id}:${reportType}`);

  const dirtyRef = React.useRef(dirty);
  const formRef = React.useRef(form);
  dirtyRef.current = dirty;
  formRef.current = form;

  React.useEffect(() => {
    const scope = `${siteId}:${item.id}:${reportType}`;
    if (scopeRef.current === scope && (dirtyRef.current || savePromiseRef.current)) return;
    scopeRef.current = scope;
    setForm(seedSetupForm(item, reportType, typedPreferences, projectIntent, site, sed));
    touchedKeysRef.current = new Set();
    setDirty(false);
    setError(null);
  }, [siteId, item.id, reportType, typedPreferences, projectIntent, site, sed]);

  const persistForm = React.useCallback(async function save(): Promise<boolean> {
    if (savePromiseRef.current) {
      const ok = await savePromiseRef.current;
      if (!ok) return false;
      return save(); // Also flush edits made while the previous save was running.
    }
    const currentForm = formRef.current;
    if (!dirtyRef.current) return true;
    const setupType = setupReportTypeForRequirement(item.id) ?? reportType;
    setSaving(true);
    setError(null);
    const pending = (async (): Promise<boolean> => {
      try {
        const touchedFields = Object.fromEntries(
          Object.entries(currentForm.fields).filter(([key]) => touchedKeysRef.current.has(key)),
        );
        const next = setupFormValuesToPreferences(
          setupType,
          touchedFields,
          useStore.getState().siteRag.sites.find(s => s.id === siteId)?.typedPreferencesDefaults?.[setupType]
            ?? typedPreferences?.[setupType],
        );
        await updateSiteTypedPreferencesDefault(siteId, setupType, next);
        if (formRef.current === currentForm) {
          dirtyRef.current = false;
          touchedKeysRef.current = new Set();
          setDirty(false);
        }
        onSaved?.();
        return true;
      } catch (e) {
        setError((e as Error)?.message ?? 'Failed to save setup');
        return false;
      } finally {
        setSaving(false);
      }
    })();
    savePromiseRef.current = pending;
    const ok = await pending;
    savePromiseRef.current = null;
    return ok && (dirtyRef.current ? save() : true);
  }, [item.id, onSaved, reportType, siteId, typedPreferences, updateSiteTypedPreferencesDefault]);

  React.useImperativeHandle(ref, () => ({
    saveIfDirty: persistForm,
  }), [persistForm]);

  if (editableFields.length === 0) return null;

  const setFieldValue = (key: string, value: string | number | boolean) => {
    touchedKeysRef.current.add(key);
    formRef.current = { ...formRef.current, fields: { ...formRef.current.fields, [key]: value } };
    dirtyRef.current = true;
    setForm(formRef.current);
    setDirty(true);
  };

  const handleSave = async () => {
    await persistForm();
  };

  return (
    <div
      className="rounded-sm border border-border-subtle bg-surface-1/60 p-2.5 space-y-2"
      data-testid={testId}
    >
      <p className="text-[10px] text-text-muted leading-snug">
        Edit report setup — saved to site defaults and used by the wizard and report generation.
      </p>
      {editableFields.map(fieldDef => (
        <AuthoredFieldInput
          key={fieldDef.key}
          field={fieldDef}
          value={form.fields[fieldDef.key]}
          onChange={v => setFieldValue(fieldDef.key, v)}
        />
      ))}
      {error && (
        <p className="text-[10px] text-danger" data-testid="coverage-inline-setup-error">
          {error}
        </p>
      )}
      <button
        type="button"
        disabled={!dirty || saving}
        onClick={() => void handleSave()}
        className="rounded-sm border border-primary/40 bg-primary/10 px-2 py-1 text-[10px] font-semibold text-primary hover:bg-primary/15 disabled:opacity-50"
        data-testid={testId === 'coverage-requirement-inline-setup' ? 'coverage-inline-setup-save' : `${testId}-save`}
      >
        {saving ? 'Saving…' : 'Save setup'}
      </button>
    </div>
  );
});

RequirementInlineSetupForm.displayName = 'RequirementInlineSetupForm';
